import { useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import {
  computeJobEconomics,
  aggregateBySource,
  aggregateByRep,
  generateRevenueAlerts,
  computeRevenueSummary,
  type JobEconomics,
} from "@/data/revenueIntelligence";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import {
  DollarSign, TrendingUp, AlertTriangle, Target,
  BarChart3, Users, Megaphone, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import { cn } from "@/lib/utils";

const fmt = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

/** Current logged-in sales rep — wired to auth later */
const CURRENT_REP = "Mike Johnson";

export default function RevenueIntelligence() {
  const { jobs } = useAppData();
  const fin$ = useFinancialPermissions();
  const navigate = useNavigate();

  const scopedJobs = useMemo(
    () => (fin$.scopedToOwn ? jobs.filter((j) => j.salesRep === CURRENT_REP) : jobs),
    [jobs, fin$.scopedToOwn],
  );
  const showMargin = fin$.viewMargin;
  const showInternalCost = fin$.viewInternalCost;
  const showRepLeaderboard = fin$.viewRepLeaderboard;

  const summary = useMemo(() => computeRevenueSummary(scopedJobs), [scopedJobs]);
  const econs = useMemo(() => scopedJobs.map(computeJobEconomics), [scopedJobs]);
  const sources = useMemo(() => aggregateBySource(scopedJobs), [scopedJobs]);
  const reps = useMemo(() => aggregateByRep(scopedJobs), [scopedJobs]);
  const alerts = useMemo(() => generateRevenueAlerts(scopedJobs), [scopedJobs]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Revenue Intelligence</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {fin$.scopedToOwn ? "Your own jobs · personal revenue view" : "Operational financial intelligence across all jobs"}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={DollarSign} label="Total Revenue" value={fmt(summary.totalRevenue)} color="text-primary" />
        {showMargin && <KPI icon={TrendingUp} label="Net Profit" value={fmt(summary.totalProfit)} color="text-success" />}
        {showMargin && <KPI icon={Target} label="Avg Margin" value={pct(summary.avgMargin)} color={summary.avgMargin < 0.2 ? "text-destructive" : "text-success"} />}
        <KPI icon={Megaphone} label="Marketing Spend" value={fmt(summary.totalMarketingCost)} color="text-info" />
        <KPI icon={AlertTriangle} label="At-Risk Revenue" value={fmt(summary.atRiskRevenue)} color="text-destructive" />
      </div>

      {alerts.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Revenue Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map(a => (
              <div key={a.id} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
                <div>
                  <Badge variant={a.severity === "critical" ? "destructive" : "secondary"} className="text-[10px] mb-1">
                    {a.severity}
                  </Badge>
                  <p className="text-sm text-foreground">{a.message}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.suggestedAction}</p>
                </div>
                {a.jobIds.length > 0 && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">{a.jobIds.length} job(s)</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="source">
        <TabsList>
          <TabsTrigger value="source"><BarChart3 className="h-3 w-3 mr-1" />By Source</TabsTrigger>
          {showRepLeaderboard && <TabsTrigger value="rep"><Users className="h-3 w-3 mr-1" />By Rep</TabsTrigger>}
          <TabsTrigger value="jobs"><DollarSign className="h-3 w-3 mr-1" />{fin$.scopedToOwn ? "My Jobs" : "All Jobs"}</TabsTrigger>
        </TabsList>

        <TabsContent value="source" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Source</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    {showMargin && <TableHead className="text-right">Profit</TableHead>}
                    {showMargin && <TableHead className="text-right">Avg Margin</TableHead>}
                    <TableHead className="text-right">CAC</TableHead>
                    <TableHead className="text-right">Lead Spend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map(s => (
                    <TableRow key={s.source}>
                      <TableCell className="font-medium">{s.source}</TableCell>
                      <TableCell className="text-right">{s.totalJobs}</TableCell>
                      <TableCell className="text-right">{fmt(s.totalRevenue)}</TableCell>
                      {showMargin && <TableCell className={cn("text-right", s.totalProfit < 0 && "text-destructive")}>{fmt(s.totalProfit)}</TableCell>}
                      {showMargin && <TableCell className="text-right"><MarginBadge margin={s.avgMargin} /></TableCell>}
                      <TableCell className="text-right">{s.cac > 0 ? fmt(s.cac) : "—"}</TableCell>
                      <TableCell className="text-right">{s.totalLeadCost > 0 ? fmt(s.totalLeadCost) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {showRepLeaderboard && (
        <TabsContent value="rep" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Rep</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rep</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Avg Contract</TableHead>
                    <TableHead className="text-right">Avg Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reps.map(r => (
                    <TableRow key={r.rep}>
                      <TableCell className="font-medium">{r.rep}</TableCell>
                      <TableCell className="text-right">{r.totalJobs}</TableCell>
                      <TableCell className="text-right">{fmt(r.totalRevenue)}</TableCell>
                      <TableCell className={cn("text-right", r.totalProfit < 0 && "text-destructive")}>{fmt(r.totalProfit)}</TableCell>
                      <TableCell className="text-right">{fmt(r.avgContractValue)}</TableCell>
                      <TableCell className="text-right"><MarginBadge margin={r.avgMargin} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="jobs" className="mt-4">
          <JobEconomicsTable
            econs={econs}
            onNavigate={(id) => navigate(`/jobs/${id}`)}
            showMargin={showMargin}
            showInternalCost={showInternalCost}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string; color: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={cn("h-4 w-4", color)} />
          <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-lg font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

function MarginBadge({ margin }: { margin: number }) {
  const color = margin >= 0.30 ? "text-success" : margin >= 0.15 ? "text-foreground" : "text-destructive";
  const Icon = margin >= 0.30 ? ArrowUpRight : margin >= 0.15 ? Minus : ArrowDownRight;
  return (
    <span className={cn("flex items-center justify-end gap-1 text-xs font-medium", color)}>
      <Icon className="h-3 w-3" />
      {pct(margin)}
    </span>
  );
}

function JobEconomicsTable({
  econs, onNavigate, showMargin, showInternalCost,
}: { econs: JobEconomics[]; onNavigate: (id: string) => void; showMargin: boolean; showInternalCost: boolean }) {
  const [sort, setSort] = useState<"margin" | "profit" | "revenue">(showMargin ? "margin" : "revenue");
  const sorted = useMemo(() => {
    const s = [...econs];
    if (sort === "margin") s.sort((a, b) => a.margin - b.margin);
    else if (sort === "profit") s.sort((a, b) => a.netProfit - b.netProfit);
    else s.sort((a, b) => b.contractValue - a.contractValue);
    return s;
  }, [econs, sort]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Job Economics</CardTitle>
          <div className="flex gap-1">
            {(["margin", "profit", "revenue"] as const).filter(s => showMargin || s === "revenue").map(s => (
              <button
                key={s}
                onClick={() => setSort(s)}
                className={cn(
                  "px-2 py-1 rounded text-[10px] font-medium transition-colors",
                  sort === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                {s === "margin" ? "Low Margin" : s === "profit" ? "Low Profit" : "High Revenue"}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Rep</TableHead>
              <TableHead className="text-right">Contract</TableHead>
              {showInternalCost && <TableHead className="text-right">Material</TableHead>}
              {showInternalCost && <TableHead className="text-right">Install</TableHead>}
              <TableHead className="text-right">Mkt</TableHead>
              {showMargin && <TableHead className="text-right">Profit</TableHead>}
              {showMargin && <TableHead className="text-right">Margin</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map(e => (
              <TableRow
                key={e.jobId}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onNavigate(e.jobId)}
              >
                <TableCell className="font-mono text-xs">{e.jobId}</TableCell>
                <TableCell className="text-xs">{e.customerName}</TableCell>
                <TableCell className="text-xs">{e.leadSource}</TableCell>
                <TableCell className="text-xs">{e.salesRep}</TableCell>
                <TableCell className="text-right text-xs">{fmt(e.contractValue)}</TableCell>
                {showInternalCost && <TableCell className="text-right text-xs">{fmt(e.materialCost)}</TableCell>}
                {showInternalCost && <TableCell className="text-right text-xs">{fmt(e.installCost)}</TableCell>}
                <TableCell className="text-right text-xs">{e.marketingCost > 0 ? fmt(e.marketingCost) : "—"}</TableCell>
                {showMargin && <TableCell className={cn("text-right text-xs font-medium", e.netProfit < 0 && "text-destructive")}>{fmt(e.netProfit)}</TableCell>}
                {showMargin && <TableCell className="text-right"><MarginBadge margin={e.margin} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
